import React, { useCallback, MouseEvent, useMemo, useState } from 'react';
import { gql, useMutation } from '@apollo/client';
import * as Sentry from '@sentry/react';
import styled from 'styled-components/macro';
import { ChannelVisibility, ChannelViewChannelFragment } from 'shared/types/graphql';
import { ChevronIcon } from 'assets/icons';
import { DeviceSizes } from 'shared/enums/DeviceSizes';
import { IOption, PopoverPlacement } from 'shared/components/Dropdown';
import { useHistory } from 'react-router-dom';
import { RouteBasePaths } from 'routes';
import ArchiveChannelAlertDialog from './ArchiveChannelAlertDialog';
import useClinicUser from 'shared/hooks/useClinicUser';
import { buildDirectMessageName } from '../utils/directMessages';
import { Tag } from '@televet/kibble-ui/build/components/Tag';
import { Box, HStack } from '@televet/kibble-ui/build/chakra';
import { Heading } from '@televet/kibble-ui/build/components/Heading';
import { AvatarGroupSelect, useAvatarGroupSelectOptions } from 'shared/components/Avatars/AvatarGroupSelect';
import { useToast } from '@televet/kibble-ui';
import { useTracking } from 'shared/providers/TrackingProvider';
import { TeamChatEvents } from 'shared/providers/TrackingProvider/EventNames';
import SummaryButtonHiddenPopover from 'pages/Conversations/components/ChannelView/Header/SummaryButtonHiddenPopover';
import { useSummaryProvider } from 'shared/providers/SummaryProvider';
import { Menu } from '@televet/kibble-ui/build/components/Menu';
import { Icon } from '@televet/kibble-ui/build/components/Icon';

enum TeamChannelMenuOption {
  EditChannel = 'editChannel',
  ArchiveChannel = 'archiveChannel',
  LeaveChannel = 'leaveChannel',
  SummarizeConversation = 'summarizeConversation',
}

const UPDATE_TEAM_CHANNEL_ASSIGNEES = gql`
  mutation updateTeamChannelAssignees($where: ChannelWhereUniqueInput!, $data: ChannelUpdateInput!) {
    updateTeamChannel2(where: $where, data: $data) {
      id
      assignees {
        id
      }
    }
  }
`;

interface ITeamChannelToolbarProps {
  channel: ChannelViewChannelFragment;
  refetchChannels: () => void;
  openEditChannelModal?: () => void;
}

const TeamChannelToolbar = ({
  channel,
  refetchChannels,
  openEditChannelModal,
}: ITeamChannelToolbarProps): JSX.Element => {
  const toast = useToast();
  const history = useHistory();
  const { clinicUser } = useClinicUser();
  const [channelIdToArchive, setChannelIdToArchive] = useState<string | null>(null);
  const isChannelPrivate = channel.channelVisibility === ChannelVisibility.Private;
  const isChannelPublic = channel.channelVisibility === ChannelVisibility.Public;
  const isDirectMessage = channel.channelVisibility === ChannelVisibility.DirectMessage;
  const [exitingChannel, setExitingChannel] = useState<boolean>(false);
  const { fetchSummary } = useSummaryProvider();
  const { trackEvent } = useTracking();

  const [updateTeamChannelAssignees, { loading: isUpdatingTeamChannelAssignees }] = useMutation(
    UPDATE_TEAM_CHANNEL_ASSIGNEES,
    {
      onError: (error) => {
        console.error(error);
        Sentry.captureException(error);
        toast({
          status: 'error',
          description: 'Whoops! An error occurred while updating the team channel. Please try again.',
          duration: 5000,
        });
      },
      onCompleted: () => {
        if (exitingChannel) {
          setExitingChannel(false);
          refetchChannels();
        }
      },
    },
  );

  const assigneeUserIds = useMemo(() => channel.assignees.map(({ id }) => id) || [], [channel]);
  const assigneeUsers = useMemo(
    () => channel.channelMembers.flatMap((m) => (m.user && assigneeUserIds.includes(m.user.id) ? [m.user] : [])),
    [channel, assigneeUserIds],
  );
  const assigneeOptions = useAvatarGroupSelectOptions({
    selectedUserIds: assigneeUserIds,
  });

  const finalAssigneeOptions = useMemo(() => {
    if (channel.isActive) {
      return assigneeOptions.map((o) => ({ ...o, isDisabled: isChannelPrivate && o.isSelected }));
    }

    return assigneeOptions.map((o) => ({ ...o, isDisabled: true }));
  }, [channel.isActive, assigneeOptions, isChannelPrivate]);

  const selectedAssignees = useMemo(() => {
    return finalAssigneeOptions.filter((o) => o.isSelected).map((o) => ({ ...o, isDisabled: true }));
  }, [finalAssigneeOptions]);

  const handleChannelExit = useCallback((): void => {
    setExitingChannel(true);
    updateTeamChannelAssignees({
      variables: {
        where: { id: channel.id },
        data: {
          assignees: { set: assigneeUserIds.filter((id) => id !== clinicUser?.id).map((id) => ({ id })) },
        },
      },
    });
  }, [channel.id, assigneeUserIds, clinicUser?.id, updateTeamChannelAssignees]);

  const handleChannelMenuOptionClick = useCallback(
    (value: TeamChannelMenuOption): void => {
      if (value === TeamChannelMenuOption.EditChannel) {
        openEditChannelModal?.();
      } else if (value === TeamChannelMenuOption.ArchiveChannel) {
        setChannelIdToArchive(channel.id);
      } else if (value === TeamChannelMenuOption.LeaveChannel) {
        handleChannelExit();
      } else if (value === TeamChannelMenuOption.SummarizeConversation) {
        fetchSummary?.();
      }
    },
    [channel.id, openEditChannelModal, handleChannelExit, fetchSummary],
  );

  const menuItems = useMemo(() => {
    return [
      {
        label: `Summarize conversation`,
        value: TeamChannelMenuOption.SummarizeConversation,
        leftElement: <Icon name="magic" />,
        onSelect: (): void => handleChannelMenuOptionClick(TeamChannelMenuOption.SummarizeConversation),
      },
      ...(!isDirectMessage
        ? [
          {
            label: `Edit channel`,
            value: TeamChannelMenuOption.EditChannel,
            isDisabled: !channel.isActive,
            leftElement: <Icon name="pen" />,
            onSelect: (): void => handleChannelMenuOptionClick(TeamChannelMenuOption.EditChannel),
          },
        ]
        : []),
      {
        label: `${isDirectMessage ? 'Close conversation' : 'Archive channel'}`,
        value: TeamChannelMenuOption.ArchiveChannel,
        isDisabled: !channel.isActive,
        leftElement: <Icon name="archive" />,
        onSelect: (): void => handleChannelMenuOptionClick(TeamChannelMenuOption.ArchiveChannel),
      },
      ...(isChannelPrivate
        ? [
          {
            label: `Leave channel`,
            value: TeamChannelMenuOption.LeaveChannel,
            isDisabled: !channel.isActive,
            leftElement: <Icon name="arrowLeft" />,
            onSelect: (): void => handleChannelMenuOptionClick(TeamChannelMenuOption.LeaveChannel),
          },
        ]
        : []),
    ];
  }, [channel.isActive, handleChannelMenuOptionClick, isChannelPrivate, isDirectMessage]);

  const handleAssigneeToggle = useCallback(
    (e: MouseEvent, option: IOption) => {
      if (isUpdatingTeamChannelAssignees) return;
      let newAssigneeUserIds = [];
      if (!option.isSelected) {
        newAssigneeUserIds = assigneeUserIds.concat(option.value.id);
        trackEvent(TeamChatEvents.ChannelAddAssignee);
      } else {
        newAssigneeUserIds = assigneeUserIds.filter((id) => id !== option.value.id);
      }

      if (assigneeUserIds.includes(clinicUser?.id || '') && !newAssigneeUserIds.includes(clinicUser?.id || '')) {
        setExitingChannel(true);
      }


      updateTeamChannelAssignees({
        variables: {
          where: { id: channel.id },
          data: {
            assignees: { set: newAssigneeUserIds.map((id) => ({ id })) },
          },
        },
      });
    },
    [isUpdatingTeamChannelAssignees, assigneeUserIds, clinicUser?.id, updateTeamChannelAssignees, channel.id, trackEvent],
  );

  const avatarGroupSelectProps = {
    addButtonTooltip: `Add ${!isChannelPrivate ? 'and remove' : ''} participants`,
    showRemoveButton: isChannelPublic && !!channel.isActive,
  };

  const directMessageName = useMemo(
    () => buildDirectMessageName(assigneeUsers, clinicUser?.id || ''),
    [clinicUser, assigneeUsers],
  );

  return (
    <TeamChannelToolbarContainer>
      <ToolbarSection>
        <ReturnButton onClick={(): void => history.push(RouteBasePaths.Team)}>
          <ChevronIcon />
        </ReturnButton>
        <HStack>
          <StyledHeading fontSize="lg" fontWeight="bold">
            {isDirectMessage ? directMessageName : channel.friendlyName || 'Unnamed Channel'}
          </StyledHeading>
          {!channel.isActive && <Tag label="Archived" backgroundColor="background.warning" color="text.warning" />}
        </HStack>
      </ToolbarSection>
      <ToolbarSection>
        <AssigneesContainer>
          <Label>Participants:</Label>
          <AvatarGroupSelect
            options={isDirectMessage ? selectedAssignees : finalAssigneeOptions}
            popoverPlacement={PopoverPlacement.BottomEnd}
            closeOnClick={false}
            onToggle={handleAssigneeToggle}
            isAddingAvatar={!isDirectMessage}
            {...avatarGroupSelectProps}
          />
        </AssigneesContainer>
        <SummaryButtonHiddenPopover
          anchor={
            <Box>
              <Menu
                buttonProps={{
                  variant: 'ghostNeutral',
                  size: 'md',
                  leftIconName: 'dotsVertical',
                  showRightIcon: false,
                }}
                isLazy
                containerProps={{
                  placement: 'left-start',
                }}
                listProps={{
                  menuItems,
                }}
              />
            </Box>
          }
        />
        <ArchiveChannelAlertDialog
          channelId={channelIdToArchive}
          onClose={(): void => setChannelIdToArchive(null)}
          channelVisibility={channel.channelVisibility || ChannelVisibility.Public}
        />
      </ToolbarSection>
    </TeamChannelToolbarContainer>
  );
};

const TeamChannelToolbarContainer = styled.div`
  flex: 0 0 auto;
  height: 65px;
  border-bottom: 1px solid #e8e9e9;
  padding: 0 20px 0 30px;
  display: flex;
  position: relative;
  align-items: center;
  justify-content: space-between;
  z-index: 999;

  @media (${DeviceSizes.MobileMaxWidth}) {
    width: 100%;
    padding: 0 15px;
  }
`;

const ReturnButton = styled.button`
  margin: 0 15px 0 0;
  background: transparent;
  border: 2px solid #6b7081;
  border-radius: 100px;
  justify-content: center;
  align-items: center;
  height: 36px;
  width: 36px;
  opacity: 0.5;
  transition: transform ease-in-out 0.2s;
  display: none;
  cursor: pointer;

  @media (max-width: 360px) {
    border: none;
    margin: 0 0 0 4px;
  }

  @media (${DeviceSizes.MobileMaxWidth}) {
    display: flex;
  }

  svg {
    height: 18px;
    width: auto;
  }

  &:hover {
    transform: scale(1.1);
    opacity: 1;
    background: hsl(190, 50%, 45%);
    border: none;

    svg > path {
      fill: #fefefe;
    }
  }
`;

const ToolbarSection = styled.div`
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  position: relative;
`;

const StyledHeading = styled(Heading)`
  @media (${DeviceSizes.MobileMaxWidth}) {
    max-width: 160px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
`;

const AssigneesContainer = styled.div`
  align-items: center;
  display: flex;
  margin-right: 20px;

  @media (${DeviceSizes.MobileMaxWidth}) {
    margin-right: 12px;
  }
`;

const Label = styled.div`
  font-size: 14px;
  font-weight: 600;
  margin-right: 12px;

  @media (${DeviceSizes.MobileMaxWidth}) {
    display: none;
  }
`;

export default TeamChannelToolbar;
